import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { X, Search, ShoppingBag, Loader2 } from 'lucide-react';
import { getProducts } from '../api/product.api';

// Lazy Image Component with IntersectionObserver
const LazyImage = ({ src, alt, className, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px', threshold: 0.01 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {isInView ? (
        <>
          {!isLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          )}
          <img
            src={src}
            alt={alt}
            onLoad={() => setIsLoaded(true)}
            className={`${className} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            {...props}
          />
        </>
      ) : (
        <div className={`${className} bg-gray-200 animate-pulse`} />
      )}
    </div>
  );
};

const ProductSelectionModal = ({ isOpen, onClose, onSelect }) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const observerTarget = useRef(null);
  const containerRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      // Only reset pagination if search actually changed (not on initial mount)
      if (search !== debouncedSearch) {
        setCurrentPage(1);
        setProducts([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Fetch products with pagination (only load first page initially)
  const fetchProducts = useCallback(async (page = 1, append = false) => {
    const limit = 20; // Smaller page size for better performance
    
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await getProducts(page, limit, debouncedSearch);
      
      console.log('ProductSelectionModal - API Response:', response);
      
      // Extract products from response
      // API returns: { products: [...], pagination: {...} }
      let productsArray = [];
      let paginationInfo = null;
      
      if (Array.isArray(response)) {
        // Direct array response (legacy support)
        productsArray = response;
        setHasNextPage(false);
        setTotalProducts(response.length);
      } else if (response && Array.isArray(response.products)) {
        // Standard response structure: { products: [...], pagination: {...} }
        productsArray = response.products;
        paginationInfo = response.pagination;
      } else if (response && response.data && Array.isArray(response.data.products)) {
        // Nested response structure: { data: { products: [...], pagination: {...} } }
        productsArray = response.data.products;
        paginationInfo = response.data.pagination;
      } else {
        console.warn('ProductSelectionModal - Unexpected response structure:', response);
        productsArray = [];
      }
      
      console.log('ProductSelectionModal - Extracted products:', productsArray.length);
      
      if (paginationInfo) {
        setHasNextPage(paginationInfo.hasNextPage || false);
        setTotalProducts(paginationInfo.totalProducts || paginationInfo.total || productsArray.length);
      } else {
        // If no pagination info, assume no more pages if we got fewer than limit
        setHasNextPage(productsArray.length === limit);
        if (page === 1) {
          setTotalProducts(productsArray.length);
        }
      }
      
      if (append) {
        setProducts(prev => [...prev, ...productsArray]);
      } else {
        setProducts(productsArray);
      }
      
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (!append) {
        setProducts([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [debouncedSearch]);

  // Initial load when modal opens and when search changes
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal closes
      setProducts([]);
      setCurrentPage(1);
      setSearch('');
      setDebouncedSearch('');
      return;
    }

    // Fetch products when modal opens or when search changes
    fetchProducts(1, false);
  }, [isOpen, debouncedSearch, fetchProducts]);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!hasNextPage || isLoadingMore || isLoading || !isOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore) {
          fetchProducts(currentPage + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isLoadingMore, isLoading, currentPage, isOpen, fetchProducts]);

  // Client-side filtering is now minimal since we filter on server
  // But we still do a quick filter for any edge cases
  const filteredProducts = useMemo(() => {
    if (!debouncedSearch) return products;
    
    const searchLower = debouncedSearch.toLowerCase();
    return products.filter(p =>
      p && p.name && (
        p.name.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower))
      )
    );
  }, [products, debouncedSearch]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-purple-100">
        <div className="flex justify-between items-center p-6 border-b border-purple-100">
          <div className="flex items-center gap-2">
            <ShoppingBag size={24} className="text-pink-500" />
            <h2 className="text-xl font-bold text-gray-800">Choose a Product</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-6" ref={containerRef}>
          <div className="mb-4 flex items-center gap-2">
            <Search size={18} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 border border-purple-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            {totalProducts > 0 && (
              <span className="text-sm text-gray-500">
                {totalProducts} {totalProducts === 1 ? 'product' : 'products'}
              </span>
            )}
          </div>
          {isLoading && products.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-purple-500" size={32} />
            </div>
          ) : filteredProducts.length === 0 && !isLoading ? (
            <div className="text-center text-gray-500 py-8">
              {products.length === 0 ? "No products found." : "No products match your search."}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                {filteredProducts.map(product => (
                  <button
                    key={product._id}
                    onClick={() => { onSelect(product); onClose(); }}
                    className="flex items-center gap-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-xl shadow hover:shadow-lg transition-all duration-200 hover:scale-105 text-left"
                  >
                    <LazyImage
                      src={product.image || product.images?.original?.imgbbUrl || product.images?.original?.url || 'https://via.placeholder.com/80x80?text=No+Image'}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-lg border flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-purple-800 text-lg mb-1 truncate">{product.name}</div>
                      <div className="text-gray-600 text-sm line-clamp-2">{product.description}</div>
                    </div>
                  </button>
                ))}
                {/* Intersection Observer target for infinite scroll */}
                {hasNextPage && (
                  <div ref={observerTarget} className="col-span-full flex justify-center py-4">
                    {isLoadingMore && (
                      <div className="flex items-center gap-2 text-purple-600">
                        <Loader2 className="animate-spin" size={20} />
                        <span className="text-sm">Loading more products...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSelectionModal; 